import { MRT_ColumnDef, MRT_RowData } from "material-react-table";
import { PrimaryGrid } from "./PrimaryGrid";
import { useGridSortUrlSync } from "../hooks/gridUrlSync/useGridSortUrlSync";
import { useGridPaginationUrlSync } from "../hooks/gridUrlSync/useGridPaginationUrlSync";
import { useGridFiltersUrlSync } from "../hooks/gridUrlSync/useGridFiltersUrlSync";
import { GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { AppSearch } from "src/api/_common/hydra/search";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { type HTMLAttributes, type ReactNode, useMemo } from "react";
import type { MRTGridAppearance } from "@foodpilot/foods";
import type { TableAction } from "../headers/GridTableActions";

type Props<Data extends MRT_RowData> = {
    readonly gridKey: string;
    columns: MRT_ColumnDef<Data, any>[];
    data: Data[];

    columnsConfig: GridColumnsType<Data>;

    isLoading: boolean;

    menuItems?: MenuItemType<Data>[];
    enableRowActions?: boolean;
    tableActions?: TableAction[];
    enableColumnsAction?: boolean;

    pagination?: {
        page?: string;
        itemsPerPage?: string;
    };
    totalItems: number;

    actionHeader?: string;
    mobileTableMode?: "compact" | "scroll";
    enableColumnResizing?: boolean;
    appearance?: MRTGridAppearance;
    emptyMessage?: string;
    renderEmptyState?: (actions: { resetFilters: () => void }) => ReactNode;
    onRowClick?: (row: Data) => void;
    enableViews?: boolean;
    viewTabsLeading?: ReactNode;
    tablePanelProps?: Pick<HTMLAttributes<HTMLDivElement>, "id" | "role" | "aria-labelledby">;

    searchParams?: AppSearch;
};
export const AutoGrid = <Data extends MRT_RowData>(props: Props<Data>) => {
    const {
        gridKey,
        columns,
        columnsConfig,
        data,
        isLoading = false,

        menuItems,
        enableRowActions,
        tableActions,
        enableColumnsAction,
        pagination,
        searchParams = {},
        totalItems,
        actionHeader,
        mobileTableMode,
        enableColumnResizing,
        appearance,
        emptyMessage,
        renderEmptyState,
        onRowClick,
        enableViews = true,
        viewTabsLeading,
        tablePanelProps,
    } = props;
    const { page = "1", itemsPerPage = "20" } = pagination || {};

    // We don't need to give isLoading here because this data immediately comes from
    // the URL
    const gridSorting = useGridSortUrlSync();
    const gridPagination = useGridPaginationUrlSync({
        defaultValue: {
            page,
            itemsPerPage,
        },
    });

    // Add tag columns to defaultValues for URL sync
    // Tag columns are dynamic and not in backend's hydra:search, so we add them manually
    const defaultValuesWithTags = useMemo(() => {
        const tagColumns = columns.filter((col) => col.id?.startsWith("tag_"));
        const tagDefaults = tagColumns.reduce(
            (acc, col) => {
                if (col.id) {
                    acc[col.id] = { value: [] }; // Default to empty array for multi-select
                }
                return acc;
            },
            {} as typeof searchParams,
        );
        return { ...searchParams, ...tagDefaults };
    }, [searchParams, columns]);

    // Here, we need the isLoading for the useEffect inside BECAUSE this data comes
    // From the Backend API
    const gridFilters = useGridFiltersUrlSync({
        defaultValues: defaultValuesWithTags,
        isLoading,
    });

    return (
        <PrimaryGrid
            appearance={appearance}
            enableViews={enableViews}
            viewTabsLeading={viewTabsLeading}
            tablePanelProps={tablePanelProps}
            isLoading={isLoading}
            gridKey={gridKey}
            columns={columns}
            data={data}
            menuItems={menuItems}
            enableRowActions={enableRowActions}
            tableActions={tableActions}
            enableColumnsAction={enableColumnsAction}
            filters={gridFilters}
            sort={gridSorting}
            pagination={{
                totalItems,
                ...gridPagination,
            }}
            columnConfig={columnsConfig}
            actionHeader={actionHeader}
            mobileTableMode={mobileTableMode}
            enableColumnResizing={enableColumnResizing}
            emptyMessage={emptyMessage}
            renderEmptyState={renderEmptyState}
            onRowClick={onRowClick}
        />
    );
};
